import { faro } from "@grafana/faro-web-sdk";
import * as Sentry from "@sentry/browser";
import * as React from "react";

import { useAccountAndUser } from "@zapier/reporting/src/hooks";

export interface UserInfoProviderProps {
  children: React.ReactNode;
}

export type UserInfo = ReturnType<typeof useAccountAndUser> | undefined;

export const userInfoContext = React.createContext<UserInfo>(undefined);

export function UserInfoProvider(props: UserInfoProviderProps) {
  const user = useAccountAndUser();

  // Sync user data with Faro and Sentry.
  React.useEffect(() => {
    if (user.isLoading) {
      // Account switches return a loading state until data for the new account
      // arrives. Clear the prior account's plan in the meantime.
      faro.api.resetUser();
      return;
    }

    // Add the plan name to Faro without sending additional user PII. The
    // receiver exposes this as the parsed log field `user_attr_userPlan` for
    // Loki filtering and grouping.
    if (user.plan?.name) {
      faro.api.setUser({
        attributes: {
          userPlan: user.plan.name,
        },
      });
    } else {
      faro.api.resetUser();
    }

    Sentry.setUser({
      accountId: user.accountId,
      id: user.userId,
    });
  }, [
    user.accountId,
    user.email,
    user.fullName,
    user.isLoading,
    user.plan?.name,
  ]);

  return (
    <userInfoContext.Provider value={user}>
      {props.children}
    </userInfoContext.Provider>
  );
}
